import React, { useEffect, useState } from 'react';
import { EnvelopeIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';

export type Canal = 'mail' | 'whatsapp';

export interface Envio {
  canal: Canal;
  destino?: string | null;
  createdAt?: string;
  firstName?: string | null;
  lastName?: string | null;
}

const fechaHora = (v?: string) => {
  if (!v) return '';
  const d = new Date(String(v).replace(' ', 'T').replace(/\+00$/, 'Z'));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
};

/**
 * Envío de un documento (informe de servicio / resumen mensual) por mail o
 * WhatsApp, con lo ya mandado abajo. Va en el pie de DocumentoDialog.
 */
const EnviarDocumento: React.FC<{
  mailInicial?: string;
  telefonoInicial?: string;
  /** El cliente no tiene mail cargado: ofrecer guardarlo */
  ofrecerGuardarMail?: boolean;
  bloqueo?: string | null;
  envios: Envio[];
  onEnviar: (canal: Canal, destino: string, guardarMail: boolean) => Promise<void>;
}> = ({ mailInicial = '', telefonoInicial = '', ofrecerGuardarMail, bloqueo, envios, onEnviar }) => {
  const [canal, setCanal] = useState<Canal>(mailInicial || !telefonoInicial ? 'mail' : 'whatsapp');
  const [mail, setMail] = useState(mailInicial);
  const [telefono, setTelefono] = useState(telefonoInicial);
  const [guardarMail, setGuardarMail] = useState(true);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { setMail(mailInicial); }, [mailInicial]);
  useEffect(() => { setTelefono(telefonoInicial); }, [telefonoInicial]);

  const destino = canal === 'mail' ? mail.trim() : telefono.trim();
  const valido = canal === 'mail' ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(destino) : destino.replace(/\D/g, '').length >= 8;

  const enviar = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!valido || bloqueo) return;
    setEnviando(true);
    try { await onEnviar(canal, destino, canal === 'mail' && !!ofrecerGuardarMail && guardarMail); } finally { setEnviando(false); }
  };

  return (
    <form onSubmit={enviar} className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">Enviar al cliente</span>
        <div className="grid grid-cols-2 rounded-md border border-input bg-background p-0.5">
          {([['mail', 'Mail', EnvelopeIcon], ['whatsapp', 'WhatsApp', ChatBubbleLeftRightIcon]] as const).map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => setCanal(k)}
              aria-pressed={canal === k}
              className={cn('inline-flex items-center justify-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                canal === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      </div>
      {canal === 'mail' ? (
        <Input type="email" value={mail} onChange={(e) => setMail(e.target.value)} placeholder="mail del cliente" className="h-9" />
      ) : (
        <Input inputMode="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="teléfono con característica" className="h-9" />
      )}
      {canal === 'mail' && ofrecerGuardarMail && (
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={guardarMail} onCheckedChange={(v) => setGuardarMail(Boolean(v))} />
          Guardar este mail en la ficha del cliente
        </label>
      )}
      <Button type="submit" className="h-9 w-full" disabled={!valido || enviando || !!bloqueo}>
        <PaperAirplaneIcon className="mr-1.5 h-4 w-4" />
        {enviando ? 'Enviando…' : canal === 'mail' ? 'Enviar por mail' : 'Enviar por WhatsApp'}
      </Button>
      {bloqueo && <p className="text-xs text-destructive">{bloqueo}</p>}
      {envios.length > 0 && (
        <ul className="space-y-1 pt-1 text-xs text-muted-foreground">
          {envios.slice(0, 3).map((e, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <CheckCircleIcon className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <span className="min-w-0 break-words">
                {e.canal === 'mail' ? 'Mail' : 'WhatsApp'} a {e.destino} · {fechaHora(e.createdAt)}
                {e.firstName ? ` · ${[e.firstName, e.lastName].filter(Boolean).join(' ')}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};

export default EnviarDocumento;
